import React, { useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2 } from 'lucide-react';

/**
 * Employee-facing modal to submit an Attendance Regularization request.
 * Wired to POST /api/my/attendance/regularization. Server enforces the
 * 30-day rolling window + "not-already-present" rule + duplicate-guard,
 * so this dialog is intentionally forgiving on the frontend.
 */
export const RegularizeAttendanceDialog = ({ open, onOpenChange, row, onSubmitted }) => {
  const [checkIn, setCheckIn] = useState('09:00');
  const [checkOut, setCheckOut] = useState('18:00');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { toast.error('Please add a reason'); return; }
    if (!checkIn) { toast.error('Check-in time is required'); return; }
    setSaving(true);
    try {
      const res = await api.post('/my/attendance/regularization', {
        date: row?.date, check_in: checkIn, check_out: checkOut, reason: reason.trim(),
      });
      if (res.data?.success) {
        toast.success('Regularization request submitted — HR will review shortly');
        onSubmitted?.(res.data.data);
        onOpenChange(false);
        setReason('');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit request');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="regularize-dialog">
        <DialogHeader>
          <DialogTitle>Regularize Attendance</DialogTitle>
          <p className="text-xs text-muted-foreground pt-1">
            Missed a check-in or forgot to punch out? Submit a correction for HR review.
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Date</Label>
            <div className="text-sm font-medium">{row?.date}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="reg-check-in">Check-in *</Label>
              <Input id="reg-check-in" type="time" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} required data-testid="reg-check-in" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-check-out">Check-out</Label>
              <Input id="reg-check-out" type="time" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} data-testid="reg-check-out" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-reason">Reason *</Label>
            <Textarea
              id="reg-reason" rows={3} maxLength={500}
              placeholder="e.g. On-site meeting at client office — forgot to punch in"
              value={reason} onChange={(e) => setReason(e.target.value)} required
              data-testid="reg-reason"
            />
            <p className="text-[10px] text-muted-foreground text-right">{reason.length}/500</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="reg-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="reg-submit">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Submit request
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RegularizeAttendanceDialog;
